import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { CostChangePct } from "@/accounts-payable/common";
import {
  AP_ACTION_TYPE,
  type ApAgentAction,
  type ApPoLineItem,
} from "@/accounts-payable/types";
import {
  formatDollars,
  stJobUrl,
  stPricebookItemUrl,
  stPurchaseOrderUrl,
} from "@/accounts-payable/utils";
import { LineItemList } from "./line-item-list";
import {
  StLink,
  StRecord,
  RecordField,
  RecordFieldGrid,
  NoStCard,
} from "./record-fields";
import { sentenceCase, getPoRecordFields } from "./utils";
/** What ServiceTitan object an action targets, with links + current/proposed context. */
export const TargetObject = ({ action }: { action: ApAgentAction }) => {
  const p = action.payload;
  const jobLink = (id: number) => (
    <StLink url={stJobUrl(id)} label={`Open job #${id} in ServiceTitan`}>
      Job #{id}
    </StLink>
  );
  const poLink = (id: number) => (
    <StLink
      url={stPurchaseOrderUrl(id)}
      label={`Open purchase order #${id} in ServiceTitan`}
    >
      PO #{id}
    </StLink>
  );
  const vendorName = (
    <Typography variant="bodyW2">
      {p.st_vendor_name}{" "}
      <span className="text-tertiary-text">#{p.st_vendor_id}</span>
    </Typography>
  );

  switch (action.action_type) {
    case AP_ACTION_TYPE.matchJob:
      return <StRecord label="Job" link={jobLink(p.st_job_external_id)} />;
    case AP_ACTION_TYPE.linkVendor:
      return <StRecord label="Vendor" name={vendorName} />;
    case AP_ACTION_TYPE.mergeVendor:
      return (
        <StRecord label="Vendor" name={vendorName}>
          {/* Names the vendor as it appears in ST (`st_vendor_name`), not
              the "display name" of the surviving Volca record
              (`target_vendor_name`); a display name that looks "wrong"
              would only invite rejecting a functionally correct merge. */}
          <Typography variant="bodyW2" textColor="secondary">
            Volca already has a vendor record linked to {p.st_vendor_name}. This
            invoice's vendor "{p.source_vendor_name}" is merged into that record
            and kept as an alias, so future invoices under that name still
            match. ServiceTitan is not changed.
          </Typography>
        </StRecord>
      );
    case AP_ACTION_TYPE.updatePricebookCost:
      return (
        <StRecord
          label={`Pricebook ${p.pricebook_item_type ?? "item"}`}
          link={
            <StLink
              url={stPricebookItemUrl(
                p.pricebook_item_type,
                p.pricebook_item_external_id,
              )}
              label={`Open ${p.sku} in ServiceTitan`}
            >
              {p.sku}
            </StLink>
          }
        >
          <RecordFieldGrid>
            <RecordField label="Current cost">
              {formatDollars(p.current_cost)}
            </RecordField>
            <RecordField label="Recommended cost" className="col-span-2">
              <Stack direction="row" gap="sm" align="baseline">
                <Typography variant="bodyW2">
                  {formatDollars(p.proposed_cost)}
                </Typography>
                <CostChangePct
                  current={p.current_cost}
                  proposed={p.proposed_cost}
                  className="text-md"
                />
              </Stack>
            </RecordField>
          </RecordFieldGrid>
        </StRecord>
      );
    case AP_ACTION_TYPE.createPricebookItem:
      return (
        <StRecord
          label="pricebook item"
          isNew
          name={<Typography variant="bodyW2">{p.name}</Typography>}
        >
          {p.description && (
            <RecordField label="Description">{p.description}</RecordField>
          )}
          <RecordFieldGrid>
            <RecordField label="Code">{p.code}</RecordField>
            <RecordField label="Type">{sentenceCase(p.item_type)}</RecordField>
            <RecordField label="Cost">{formatDollars(p.cost)}</RecordField>
          </RecordFieldGrid>
        </StRecord>
      );
    case AP_ACTION_TYPE.updatePo: {
      const { short, wide, items } = getPoRecordFields(p.changes);
      return (
        <StRecord label="Purchase order" link={poLink(p.st_purchase_order_id)}>
          {short.length > 0 && (
            <RecordFieldGrid>
              {short.map((field) => (
                <RecordField key={field.key} label={field.label}>
                  {field.value}
                </RecordField>
              ))}
            </RecordFieldGrid>
          )}
          {wide.map((field) => (
            <RecordField key={field.key} label={field.label}>
              {field.value}
            </RecordField>
          ))}
          {items && items.length > 0 && <LineItemList items={items} />}
          {p.reason && <RecordField label="Reason">{p.reason}</RecordField>}
        </StRecord>
      );
    }
    case AP_ACTION_TYPE.createPo:
    case AP_ACTION_TYPE.createReturn: {
      const items = (p.items ?? []) as ApPoLineItem[];
      return (
        <StRecord
          label={
            action.action_type === AP_ACTION_TYPE.createPo
              ? "purchase order"
              : "return"
          }
          isNew
          link={
            p.st_purchase_order_id ? poLink(p.st_purchase_order_id) : undefined
          }
        >
          <RecordField label="Job">
            {p.st_job_external_id ? (
              jobLink(p.st_job_external_id)
            ) : (
              /* A non-job purchase: ServiceTitan files it with no job, so
                 there is nothing to link to here. */
              <Typography variant="bodyW2">No job (non-job expense)</Typography>
            )}
          </RecordField>
          {p.reason && (
            <Typography variant="bodyW2" textColor="secondary">
              {p.reason}
            </Typography>
          )}
          {items.length > 0 && <LineItemList items={items} />}
        </StRecord>
      );
    }
    case AP_ACTION_TYPE.markNonJob:
      return p.st_purchase_order_id ? (
        <StRecord label="purchase order" link={poLink(p.st_purchase_order_id)}>
          <Typography variant="bodyW2" textColor="secondary">
            Already files this invoice's cost with no job. {p.reason}
          </Typography>
        </StRecord>
      ) : (
        <NoStCard reason={p.reason} />
      );
    case AP_ACTION_TYPE.flagForReview:
    case AP_ACTION_TYPE.noAction:
      // The row's title already is the reason; nothing touches ServiceTitan.
      return null;
    default:
      return null;
  }
};
